import _ from 'lodash'
import { all, put, select, takeEvery, takeLatest } from 'redux-saga/effects'

import { validateField, validateFields } from 'common/validator'
import mixpanel, { EVENTS } from 'lib/mixpanel'

import {
  addFieldError,
  addFieldsErrors,
  clearFieldError,
  SUBMIT_ONBOARDING,
  VALIDATE_ONBOARDING_FIELD,
} from './actions'

import { postOnboardingService } from './index'

interface SubmitOnboardingPayload {
  emotionWords: { value: string[] }
  jobTitle?: { value?: string }
  personTypeId?: { value?: string }
  grades?: { value?: unknown[] }
  subjects?: { value?: unknown[] }
}

export function* submitOnboardingSaga({
  payload,
}: {
  payload: SubmitOnboardingPayload
}): Generator<unknown, void, unknown> {
  const errors = validateFields(
    payload as unknown as Record<string, { value: unknown; validations?: string }>,
  )

  if (_.isEmpty(errors)) {
    const availableEmotionWords = (yield select(
      (state: { emotionWords: { items: Array<{ id: string; name: string }> } }) =>
        state.emotionWords.items,
    )) as Array<{ id: string; name: string }>

    const onboardingData: Record<string, unknown> = {
      emotion_words: _.map((payload.emotionWords as { value: string[] }).value, (wordId) => ({
        emotion_word_id: wordId,
        actions: [],
      })),
    }

    if (payload.jobTitle?.value) {
      onboardingData.job_title_id = payload.jobTitle.value
    }

    if (payload.personTypeId?.value) {
      onboardingData.person_type_id = payload.personTypeId.value
    }

    if (payload.grades?.value) {
      onboardingData.grades = payload.grades.value
    }

    if (payload.subjects?.value) {
      onboardingData.subjects = payload.subjects.value
    }

    const emotionWords = _.map((payload.emotionWords as { value: string[] }).value, (wordId) =>
      _.find(availableEmotionWords, { id: wordId }),
    )

    yield put(
      postOnboardingService.actions.request({
        data: onboardingData,
      }),
    )
    mixpanel.track(EVENTS.COMPLETE_ONBOARDING)
    _.forEach(emotionWords, (word) => {
      if (word) {
        mixpanel.track(EVENTS.HOW_I_WANT_TO_FEEL_FROM_ONBOARDING, {
          emotion_word_id: word.id,
          emotion_word_name: word.name,
        })
      }
    })
  } else {
    yield put(addFieldsErrors(errors as Record<string, string>))
  }
}

export function* validateFieldSaga({
  payload: { field, value, validations },
}: {
  payload: { field: string; value: unknown; validations: string }
}): Generator<unknown, void, unknown> {
  const error = validateField({ field, value, fieldValidations: validations })

  if (error) {
    yield put(addFieldError({ field, error: error as string }))
  } else {
    yield put(clearFieldError({ field }))
  }
}

type ValidateFieldAction = {
  type: typeof VALIDATE_ONBOARDING_FIELD
  payload: { field: string; value: unknown; validations: string }
}
type SubmitOnboardingAction = {
  type: typeof SUBMIT_ONBOARDING
  payload: SubmitOnboardingPayload
}

export default function* saga(): Generator<unknown, void, unknown> {
  yield all([
    takeEvery<ValidateFieldAction>(VALIDATE_ONBOARDING_FIELD, validateFieldSaga),
    takeLatest<SubmitOnboardingAction>(SUBMIT_ONBOARDING, submitOnboardingSaga),
  ])
}
